import type {
  CampaignApiModel,
  CharacterStrategyApi,
  NoChatIntroScenarioApi,
  NoChatIntroScheduleApi,
  ReachOutCharacterRuleApi,
  ReachOutRuleSendWindowApi,
  ReengagementSequenceStepApi,
} from "../../../../api/campaigns";
import {
  DEFAULT_SEND_TIME_VALUE,
  type CampaignSettings,
  type EmailCampaignState,
  type ManagedEmailCampaign,
  type NoChatPromptRule,
  type NoChatScenario,
  type PremiumCharacterRule,
  type SendSchedule,
} from "../types";
import { DEFAULT_SEND_TIME_OPTIONS } from "../constants/sendTimeOptions";

const DEFAULT_RESEND_COOLDOWN_DAYS = 7;

const createDefaultCampaignState = (): EmailCampaignState => ({
  settings: {
    sequence: [],
    threadFollowUp: {
      dynamicRange: { minTime: 0, maxTime: 0, unit: "hours" },
    },
    tokenThreshold: 0,
    defaultCharacterId: "",
    sendingAddress: "",
    mailProviderName: "",
    tracking: {
      trackOpens: true,
      trackClicks: true,
      trackChatEngagement: true,
      trackReplies: true,
    },
    characterStrategy: "last-high-token",
  },
  promptLibrary: {
    noChat: {
      id: "",
      title: "",
      description: "",
      body: "",
      lastUpdated: "",
      schedule: {
        offsetValue: 0,
        offsetUnit: "hours",
        sendTimeOptionId: DEFAULT_SEND_TIME_VALUE,
      },
      triggerScenarios: [],
    },
    premium: {
      id: "",
      title: "",
      description: "",
      prompt: "",
      lastUpdated: "",
    },
    sendTimeOptions: DEFAULT_SEND_TIME_OPTIONS,
  },
  characterLibrary: [],
  batchConfigurations: [],
  batchRuns: [],
  users: [],
  scheduledEmails: [],
  premiumCriteria: { characterRules: [] },
  analytics: {
    overview: {
      totalEmailsSent: 0,
      openRate: 0,
      clickRate: 0,
      reengagementRate: 0,
      replyRate: 0,
      weeklySends: 0,
    },
    sequencePerformance: [],
    characterPerformance: [],
  },
});

const getDefaultCampaignState = (): EmailCampaignState =>
  createDefaultCampaignState();

const mapCharacterStrategy = (
  strategy?: CharacterStrategyApi
): CampaignSettings["characterStrategy"] => {
  switch (strategy) {
    case "character-with-most-conversation":
      return "most-frequent";
    case "last-character":
      return "last-high-token";
    default:
      return getDefaultCampaignState().settings.characterStrategy;
  }
};

const mapSequenceSteps = (
  steps?: ReengagementSequenceStepApi[]
): CampaignSettings["sequence"] => {
  if (!Array.isArray(steps) || steps.length === 0) {
    return getDefaultCampaignState().settings.sequence;
  }

  return steps.map((step) => ({
    id: step._id ?? (step as any).id ?? "",
    label: step.step_name,
    offsetValue: step.offsetValue,
    offsetUnit: step.offsetUnit,
    sendTimeOptionId: step.timeToSend ?? DEFAULT_SEND_TIME_VALUE,
    prompt: step.prompt,
  }));
};

const mapThreadFollowUp = (
  campaign: CampaignApiModel,
  fallback: CampaignSettings["threadFollowUp"]
): CampaignSettings["threadFollowUp"] => {
  const dynamicRange = campaign.characterReplyTiming?.dynamicRange;
  if (!dynamicRange) {
    return fallback;
  }

  const fallbackRange = fallback.dynamicRange;
  const minTime =
    typeof (dynamicRange as any).minTime === "number"
      ? (dynamicRange as any).minTime
      : typeof (dynamicRange as any).minHours === "number"
      ? (dynamicRange as any).minHours
      : fallbackRange.minTime;
  const rawMax =
    typeof (dynamicRange as any).maxTime === "number"
      ? (dynamicRange as any).maxTime
      : typeof (dynamicRange as any).maxHours === "number"
      ? (dynamicRange as any).maxHours
      : fallbackRange.maxTime;
  const unit =
    (dynamicRange as any).unit === "minutes" ? "minutes" : "hours";

  const normalizedMin = Number.isFinite(minTime) ? Math.max(0, minTime) : 0;
  const normalizedMax = Number.isFinite(rawMax)
    ? Math.max(normalizedMin, rawMax)
    : Math.max(normalizedMin, fallbackRange.maxTime);

  return {
    dynamicRange: {
      minTime: normalizedMin,
      maxTime: normalizedMax,
      unit,
    },
  };
};

const mapSendScheduleFromApi = (
  source?: NoChatIntroScheduleApi | ReachOutRuleSendWindowApi
): SendSchedule | undefined => {
  if (!source) return undefined;
  return {
    offsetValue: source.offsetValue,
    offsetUnit: source.offsetUnit,
    sendTimeOptionId: source.timeToSend ?? DEFAULT_SEND_TIME_VALUE,
  };
};

const mapNoChatPrompts = (prompts?: string[]): NoChatPromptRule[] => {
  if (!Array.isArray(prompts) || prompts.length === 0) {
    return [];
  }
  return prompts.map((prompt, index) => {
    const sanitized = prompt.trim();
    return {
      id: `prompt-${index}-${sanitized.slice(0, 8)}`,
      label: sanitized,
      enabled: true,
    };
  });
};

const mapNoChatScenarios = (
  scenarios?: NoChatIntroScenarioApi[]
): NoChatScenario[] => {
  if (!Array.isArray(scenarios) || scenarios.length === 0) {
    return [];
  }

  return scenarios.map((scenario) => ({
    id: scenario._id,
    characterId: scenario.characterId,
    from_character_id:
      scenario.from_character_id ??
      scenario.fromCharacterId ??
      scenario.characterId,
    storylineTitle: scenario.storylineTitle,
    storylineSummary: scenario.storylineSummary,
    prompts: mapNoChatPrompts(scenario.prompts),
    subject: scenario.manualEmailSubject ?? scenario.subject,
    body: scenario.manualEmailBody ?? scenario.body,
    promptInstructions: scenario.promptInstructions,
    manualEmailEnabled:
      scenario.manualEmailEnabled ?? !scenario.promptInstructions,
    manualEmailSubject: scenario.manualEmailSubject,
    manualEmailBody: scenario.manualEmailBody,
    lastUpdated:
      scenario.updatedAt ??
      scenario.createdAt ??
      new Date().toISOString(),
    maxConversationCount: scenario.maxConversationCount,
  }));
};

const mapPremiumCharacterRule = (
  rule: ReachOutCharacterRuleApi
): PremiumCharacterRule => ({
  id: rule.id,
  serverRuleId: rule._id,
  reachoutCharacterId: rule.reachoutCharacterId,
  reachoutCharacterTier: rule.reachoutCharacterTier,
  triggerCharacterIds: rule.triggerCharacterIds ?? [],
  minimumTokens: rule.minimumTokens,
  triggerAfterAnyCount: rule.triggerAfterAnyCount,
  resendCooldownDays: rule.resendCooldownDays ?? DEFAULT_RESEND_COOLDOWN_DAYS,
  sendWindow: {
    offsetValue: rule.sendWindow?.offsetValue ?? 0,
    offsetUnit: rule.sendWindow?.offsetUnit ?? "days",
    sendTimeOptionId:
      rule.sendWindow?.timeToSend ?? DEFAULT_SEND_TIME_VALUE,
  },
  promptInstructions: rule.promptInstructions,
  manualEmailEnabled: Boolean(rule.manualEmailEnabled),
  manualEmailSubject: rule.manualEmailSubject,
  manualEmailBody: rule.manualEmailBody,
});

export const mapCampaignApiToManaged = (
  campaign: CampaignApiModel
): ManagedEmailCampaign => {
  const baseState = getDefaultCampaignState();

  const selection = campaign.reengagementCharacterSelection;
  const noChatIntro = campaign.noChatIntro;
  const premiumRules =
    campaign.reachOutCharacterRules?.characterRules ?? [];

  const mappedState: EmailCampaignState = {
    ...baseState,
    settings: {
      ...baseState.settings,
      sequence: mapSequenceSteps(campaign.reengagementEmailSequence),
      threadFollowUp: mapThreadFollowUp(
        campaign,
        baseState.settings.threadFollowUp
      ),
      tokenThreshold:
        selection?.tokenThreshold ?? baseState.settings.tokenThreshold,
      characterStrategy: mapCharacterStrategy(selection?.characterStrategy),
      defaultCharacterId:
        selection?.default_fallback_character_id ??
        baseState.settings.defaultCharacterId,
    },
    promptLibrary: {
      ...baseState.promptLibrary,
      noChat: {
        ...baseState.promptLibrary.noChat,
        schedule:
          mapSendScheduleFromApi(noChatIntro?.schedule) ??
          baseState.promptLibrary.noChat.schedule,
        triggerScenarios: mapNoChatScenarios(
          noChatIntro?.scenarios ?? noChatIntro?.triggerScenarios
        ),
      },
    },
    premiumCriteria: {
      characterRules: premiumRules.map(mapPremiumCharacterRule),
    },
  };

  return {
    id: campaign._id,
    name: campaign.name,
    status: campaign.active ? "active" : "paused",
    data: mappedState,
  };
};
